import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { StatusBadge } from './StatusBadge';
import { 
  formatDate, 
  calculateDaysRemaining, 
  getRenewalStatus, 
  getRenewalStatusText,
  getRenewalStatusColor,
  getRenewalStatusIcon,
  getRenewalActionRecommendation
} from '../lib/utils';
import { Download, RefreshCw, AlertTriangle, Info, Server, Calendar, Clock } from 'lucide-react';
import { certificatesAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

export function CertificateTable({ certificates, onRefresh }) {
  const [loading, setLoading] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, certId: null, type: null });
  const [infoDialog, setInfoDialog] = useState({ open: false, cert: null });

  const handleDownload = async (certId, domain) => {
    setLoading(prev => ({ ...prev, [`download-${certId}`]: true }));
    try {
      const response = await certificatesAPI.download(certId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${domain}-certificates.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Sertifikat ${domain} berhasil diunduh`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Gagal mengunduh sertifikat');
    } finally {
      setLoading(prev => ({ ...prev, [`download-${certId}`]: false }));
    }
  };

  const handleRenewDryRun = async (certId, domain) => {
    setLoading(prev => ({ ...prev, [`renew-${certId}`]: true }));
    try {
      const response = await certificatesAPI.renewDryRun(certId);
      if (response.data.success) {
        toast.success(response.data.message || 'Simulasi pembaruan berhasil');
      } else {
        if (response.data.message && 
            (response.data.message.includes('rateLimited') || 
             response.data.message.includes('Rate limit') ||
             response.data.message.includes('Service busy'))) {
          toast.error('⏳ Rate limit dari Let\'s Encrypt. Tunggu 1-2 jam dan coba lagi.', {
            duration: 5000
          });
        } else {
          toast.error(response.data.message || 'Simulasi pembaruan gagal');
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Gagal melakukan simulasi pembaruan';
      if (errorMsg.includes('rateLimited') || 
          errorMsg.includes('Service busy') || 
          errorMsg.includes('Rate limit')) {
        toast.error('⏳ Rate limit Let\'s Encrypt. Tunggu 1-2 jam lalu coba lagi.', {
          duration: 5000
        });
      } else if (error.response?.status === 504) {
        toast.error('⏱️ Request timeout. Proses mungkin masih berjalan di server.', {
          duration: 5000
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(prev => ({ ...prev, [`renew-${certId}`]: false }));
    }
  };

  const handleRenewLive = async (certId, domain) => {
    setLoading(prev => ({ ...prev, [`renew-live-${certId}`]: true }));
    try {
      const response = await certificatesAPI.renewLive(certId);
      if (response.data.success) {
        toast.success(response.data.message || 'Pembaruan live berhasil!', {
          duration: 5000
        });
        onRefresh?.();
      } else {
        if (response.data.message && 
            (response.data.message.includes('rateLimited') || 
             response.data.message.includes('Rate limit') ||
             response.data.message.includes('Service busy'))) {
          toast.error('⏳ Rate limit dari Let\'s Encrypt. Tunggu beberapa jam dan coba lagi.', {
            duration: 6000
          });
        } else {
          toast.error(response.data.message || 'Pembaruan live gagal');
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Gagal melakukan pembaruan live';
      if (errorMsg.includes('rateLimited') || 
          errorMsg.includes('Service busy') || 
          errorMsg.includes('Rate limit')) {
        toast.error('⏳ Rate limit Let\'s Encrypt. Tunggu beberapa jam lalu coba lagi.', {
          duration: 6000
        });
      } else if (error.response?.status === 504) {
        toast.error('⏱️ Request timeout. Proses pembaruan mungkin masih berjalan di background.', {
          duration: 6000
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(prev => ({ ...prev, [`renew-live-${certId}`]: false }));
      setConfirmDialog({ open: false, certId: null, type: null });
    }
  };

  const openConfirmDialog = (certId, type) => {
    setConfirmDialog({ open: true, certId, type });
  };

  const handleConfirm = () => {
    const { certId, type } = confirmDialog;
    const cert = certificates.find(c => c.id === certId);
    if (type === 'live' && cert) {
      handleRenewLive(certId, cert.domain);
    }
  };

  const openInfoDialog = (cert) => {
    setInfoDialog({ open: true, cert });
  };

  // Render Mobile Card View with zebra striping
  const renderMobileCard = (cert, index) => {
    const daysRemaining = calculateDaysRemaining(cert.expiration_date);
    const renewalStatus = getRenewalStatus(daysRemaining);
    const renewalStatusText = getRenewalStatusText(renewalStatus);
    const renewalStatusColor = getRenewalStatusColor(renewalStatus);
    const renewalIcon = getRenewalStatusIcon(renewalStatus);
    const isEven = index % 2 === 0;

    return (
      <motion.div
        key={cert.id}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ scale: 1.01, y: -2 }}
        className="mb-2"
      >
        <Card className={`${isEven ? 'bg-muted/20' : 'bg-background'} transition-colors hover:shadow-md`}>
          <CardContent className="p-3 space-y-3">
            {/* Header - Domain & Status */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm sm:text-base truncate">{cert.domain}</h3>
                <div className="flex items-center gap-2 mt-1 text-xs sm:text-sm text-muted-foreground">
                  <Server className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">{cert.server_source}</span>
                </div>
              </div>
              <StatusBadge daysRemaining={daysRemaining} />
            </div>

            {/* Certificate Info */}
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between p-1.5 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground text-xs sm:text-sm">Kedaluwarsa:</span>
                </div>
                <span className="font-medium text-xs sm:text-sm">{formatDate(cert.expiration_date)}</span>
              </div>
              
              <div className="flex items-center justify-between p-1.5 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground text-xs sm:text-sm">Sisa Waktu:</span>
                </div>
                <span className={`font-semibold text-xs sm:text-sm ${
                  daysRemaining < 0 ? 'text-gray-600' :
                  daysRemaining <= 7 ? 'text-red-600' :
                  daysRemaining <= 30 ? 'text-orange-600' :
                  'text-green-600'
                }`}>
                  {daysRemaining < 0 ? 'Kedaluwarsa' : `${daysRemaining} hari`}
                </span>
              </div>

              <div className="flex items-center justify-between p-1.5 bg-muted/50 rounded-md">
                <span className="text-muted-foreground text-xs sm:text-sm">Info Pembaruan:</span>
                <div className="flex items-center gap-1.5">
                  <Badge className={`${renewalStatusColor} border text-xs whitespace-nowrap`}>
                    {renewalIcon} {renewalStatusText}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0"
                    onClick={() => openInfoDialog(cert)}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-1.5 pt-1.5 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownload(cert.id, cert.domain)}
                disabled={loading[`download-${cert.id}`]}
                className="w-full justify-center text-xs sm:text-sm h-8"
              >
                <Download className="h-3.5 w-3.5 mr-2" />
                {loading[`download-${cert.id}`] ? 'Mengunduh...' : 'Unduh Sertifikat'}
              </Button>
              
              <div className="grid grid-cols-2 gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRenewDryRun(cert.id, cert.domain)}
                  disabled={loading[`renew-${cert.id}`] || renewalStatus === 'safe'}
                  title={renewalStatus === 'safe' ? 'Sertifikat masih terlalu lama untuk uji pembaruan' : 'Uji pembaruan (dry-run)'}
                  className="w-full text-xs sm:text-sm h-8"
                >
                  <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                  {loading[`renew-${cert.id}`] ? 'Menguji...' : 'Uji'}
                </Button>
                
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => openConfirmDialog(cert.id, 'live')}
                  disabled={loading[`renew-live-${cert.id}`] || daysRemaining > 30}
                  title={daysRemaining > 30 ? 'Sertifikat masih > 30 hari' : 'Pembaruan live (production)'}
                  className="w-full text-xs sm:text-sm h-8"
                >
                  <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                  {loading[`renew-live-${cert.id}`] ? 'Memperbarui...' : 'Live'}
                </Button>
              </div>
            </div>

            {/* Last Updated */}
            <div className="text-xs text-muted-foreground text-center pt-1.5 border-t">
              Terakhir diperbarui: {formatDate(cert.last_updated)}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <>
      {/* Desktop Table View - Hidden on mobile with zebra striping */}
      <div className="hidden lg:block rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 h-9">
                <TableHead className="font-semibold py-1.5 text-sm">Domain</TableHead>
                <TableHead className="font-semibold py-1.5 text-sm">Server</TableHead>
                <TableHead className="font-semibold py-1.5 text-sm">Tanggal Kedaluwarsa</TableHead>
                <TableHead className="font-semibold py-1.5 text-sm">Sisa Hari</TableHead>
                <TableHead className="font-semibold py-1.5 text-sm">Status</TableHead>
                <TableHead className="font-semibold py-1.5 text-sm">Info Pembaruan</TableHead>
                <TableHead className="font-semibold py-1.5 text-sm">Terakhir Diperbarui</TableHead>
                <TableHead className="text-right font-semibold py-1.5 text-sm">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {certificates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    Tidak ada data sertifikat
                  </TableCell>
                </TableRow>
              ) : (
                certificates.map((cert, index) => {
                  const daysRemaining = calculateDaysRemaining(cert.expiration_date);
                  const renewalStatus = getRenewalStatus(daysRemaining);
                  const renewalStatusText = getRenewalStatusText(renewalStatus);
                  const renewalStatusColor = getRenewalStatusColor(renewalStatus);
                  const renewalIcon = getRenewalStatusIcon(renewalStatus);
                  const isEven = index % 2 === 0;
                  
                  return (
                    <motion.tr
                      key={cert.id}
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                      className={`
                        ${isEven ? 'bg-muted/30' : 'bg-background'}
                        hover:bg-muted/50 transition-colors
                        border-b last:border-0
                      `}
                    >
                      <TableCell className="font-medium py-1.5 text-sm">
                        <div className="max-w-xs truncate" title={cert.domain}>
                          {cert.domain}
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5 text-sm">
                        <div className="max-w-[150px] truncate" title={cert.server_source}>
                          {cert.server_source}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-1.5 text-sm">
                        {formatDate(cert.expiration_date)}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <span className={`font-semibold whitespace-nowrap text-sm ${
                          daysRemaining < 0 ? 'text-gray-600' :
                          daysRemaining <= 7 ? 'text-red-600' :
                          daysRemaining <= 30 ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                          {daysRemaining < 0 ? 'Kedaluwarsa' : `${daysRemaining} hari`}
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        <StatusBadge daysRemaining={daysRemaining} />
                      </TableCell>
                      <TableCell className="py-1.5">
                        <div className="flex items-center gap-1.5">
                          <Badge className={`${renewalStatusColor} border text-[11px] py-0 px-2 h-5 whitespace-nowrap`}>
                            {renewalIcon} {renewalStatusText}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => openInfoDialog(cert)}
                          >
                            <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs whitespace-nowrap py-1.5">
                        {formatDate(cert.last_updated)}
                      </TableCell>
                      <TableCell className="text-right py-1.5">
                        <div className="flex justify-end items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(cert.id, cert.domain)}
                            disabled={loading[`download-${cert.id}`]}
                            className="h-7 text-xs px-2.5"
                          >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            {loading[`download-${cert.id}`] ? 'Mengunduh...' : 'Unduh'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRenewDryRun(cert.id, cert.domain)}
                            disabled={loading[`renew-${cert.id}`] || renewalStatus === 'safe'}
                            title={renewalStatus === 'safe' ? 'Sertifikat masih terlalu lama untuk uji pembaruan' : 'Uji pembaruan (dry-run)'}
                            className="h-7 text-xs px-2.5"
                          >
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            {loading[`renew-${cert.id}`] ? 'Menguji...' : 'Uji Pemb.'}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => openConfirmDialog(cert.id, 'live')}
                            disabled={loading[`renew-live-${cert.id}`] || daysRemaining > 30}
                            title={daysRemaining > 30 ? 'Sertifikat masih > 30 hari, tidak direkomendasikan untuk pembaruan' : 'Pembaruan live (production)'}
                            className="h-7 text-xs px-2.5"
                          >
                            <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                            {loading[`renew-live-${cert.id}`] ? 'Memperbarui...' : 'Live'}
                          </Button>
                        </div>
                      </TableCell>
                    </motion.tr>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile Card View - Visible only on mobile with zebra striping */}
      <div className="lg:hidden space-y-2">
        {certificates.length === 0 ? (
          <Card>
            <CardContent className="text-center text-muted-foreground py-8">
              Tidak ada data sertifikat
            </CardContent>
          </Card>
        ) : (
          certificates.map((cert, index) => renderMobileCard(cert, index))
        )}
      </div>

      {/* Info Dialog */}
      <Dialog open={infoDialog.open} onOpenChange={(open) => setInfoDialog({ ...infoDialog, open })}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Informasi Status Pembaruan</DialogTitle>
            <DialogDescription>
              Detail rekomendasi pembaruan untuk {infoDialog.cert?.domain}
            </DialogDescription>
          </DialogHeader>
          {infoDialog.cert && (
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-muted/50">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Domain:</span>
                    <span className="truncate ml-2 text-right">{infoDialog.cert.domain}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Server:</span>
                    <span className="truncate ml-2 text-right">{infoDialog.cert.server_source}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Sisa Waktu:</span>
                    <span className="font-semibold">
                      {calculateDaysRemaining(infoDialog.cert.expiration_date)} hari
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Tanggal Kedaluwarsa:</span>
                    <span className="text-right">{formatDate(infoDialog.cert.expiration_date)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                <h4 className="font-semibold text-sm text-blue-900 mb-2">
                  {getRenewalStatusIcon(getRenewalStatus(calculateDaysRemaining(infoDialog.cert.expiration_date)))} 
                  {' '}{getRenewalStatusText(getRenewalStatus(calculateDaysRemaining(infoDialog.cert.expiration_date)))}
                </h4>
                <p className="text-sm text-blue-800">
                  {getRenewalActionRecommendation(calculateDaysRemaining(infoDialog.cert.expiration_date))}
                </p>
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <p>📅 <strong>Timeline Pembaruan:</strong></p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li>Hari 0-60: Aman, belum perlu pembaruan</li>
                  <li>Hari 60-82: Boleh uji pembaruan</li>
                  <li>Hari 83-89: Sudah bisa pembaruan live</li>
                  <li>Hari 90+: Kedaluwarsa!</li>
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent className="max-w-md mx-4 sm:mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Pembaruan Live</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin melakukan <strong>PEMBARUAN LIVE</strong> untuk sertifikat ini?
              <br /><br />
              <span className="text-red-600 font-semibold">
                ⚠️ Tindakan ini akan melakukan pembaruan sesungguhnya ke Let's Encrypt!
              </span>
              <br /><br />
              <span className="text-sm text-muted-foreground">
                Pastikan sertifikat memang perlu diperbarui (kurang dari 30 hari). 
                Pembaruan yang tidak perlu dapat menyebabkan rate limit.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className="w-full sm:w-auto bg-red-600 hover:bg-red-700">
              Ya, Lakukan Pembaruan Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}